import {Button, Col, Divider, Modal, Row} from "antd";
import {useGetLoggedInUserQuery} from "@mwServices/User";
import React, {useEffect, useMemo, useState} from "react";
import usePreferences from "@hooks/usePreferences";
import {useNavigate} from "react-router-dom";
import {DollarIcon} from "@components/icons/Icons";

import clz_logo from "@images/clz_logo.png";

import {
    CollectionManagementIcon,
    ContentIcon,
    CoverPriceLogoIcon,
    ImportCollectionIcon,
    ListsIcon, MarketplaceIcon,
    NewReleasesIcon,
    PriceGuideIcon,
    TrendIcon
} from "@components/icons/HomePageIcons";

const NewMemberListItem = ({IconClass, iconClass, title, description}) => {
    return (
        <div className={"modal-list-item"}>
            <IconClass className={iconClass} />
            <div className="modal-list-item-text">
                <b>{title}</b>
                {description}
            </div>
        </div>
    );
};

const HomePageNewMemberModal = ({windowDimensions, showDesktopView, bgUrl}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const loggedInQuery = useGetLoggedInUserQuery();
    const { showNewUserModal, setShowNewUserModal } = usePreferences();
    const navigate = useNavigate();

    useEffect(() => {
        if(!loggedInQuery.isLoading) {
            const loggedIn = !!(loggedInQuery && loggedInQuery.data && loggedInQuery.data.user);
            if(!loggedIn) setIsModalOpen(true);
        }
    }, [loggedInQuery]);

    const onModalClose = () => {
        setShowNewUserModal(false);
        setIsModalOpen(false);
    };
    
    const itemRowSpan = useMemo(() => {
        return showDesktopView ? 12 : 24;
    }, [showDesktopView]);


    const itemRowIconClass = useMemo(() => {
        return windowDimensions.over(1199) ? "greyCircleSmallIcon text-grey" : "greyCircleTinyIcon text-grey";
    }, [windowDimensions]);

    const itemRowGutter = useMemo(() => {
        return windowDimensions.over(1199) ? [20,20] : [12,12];
    }, [windowDimensions]);

    const backgroundStyle = useMemo(() => {
        return bgUrl ? `url("${bgUrl}") no-repeat center, #0e202d` : null;
    }, [bgUrl]);

    return (
        <Modal
            title={null}
            open={isModalOpen}
            onCancel={onModalClose}
            centered={true}
            destroyOnClose={true}
            footer={null}
            className="new-member-modal mar-top-20"
        >
            <div className="modal-header" style={backgroundStyle ? {
                background: backgroundStyle
            } : undefined}>
                <div className="modal-header-row">
                    <div className={"modal-header-line mar-right-10"}/>
                    <CoverPriceLogoIcon className={"modal-header-icon"} />
                    <div className={"modal-header-line mar-left-10"}/>
                </div>
                <div className="text-center">
                    <p className={"modal-title"}>The Ultimate Comic Book Resource</p>
                    <p className={"modal-subtitle mar-top-10"}>
                        Find a comic. See what it’s worth. Add it to your collection.<br/>
                        COVRPRICE automatically tracks the value of your entire collection for you.
                    </p>
                    <Button onClick={() => navigate("/register")} className="orangeButton mar-top-20 mar-bot-20" size="large">
                        Start Free Trial
                    </Button>
                    <br/>
                    <a href={"/plans/unlimited"} className={"text-white"}>View Membership Plans</a>
                </div>
            </div>
            <div className={"pad-20"}>
                <Row gutter={itemRowGutter as any}  >
                    <Col span={itemRowSpan}>
                        <NewMemberListItem
                            IconClass={PriceGuideIcon}
                            iconClass={itemRowIconClass}
                            title={"Price Guides & Values"}
                            description={"See real and aftermarket sales for raw and graded comics."}
                        />
                    </Col>
                    <Col span={itemRowSpan}>
                        <NewMemberListItem
                            IconClass={NewReleasesIcon}
                            iconClass={itemRowIconClass}
                            title={"New Releases"}
                            description={"Find comics coming out each week including hard to find exclusives."}
                        />
                    </Col>
                    <Col span={itemRowSpan}>
                        <NewMemberListItem
                            IconClass={CollectionManagementIcon}
                            iconClass={itemRowIconClass}
                            title={"Collection Management"}
                            description={"Track and analyze your entire collection in one place."}
                        />
                    </Col>
                    <Col span={itemRowSpan}>
                        <NewMemberListItem
                            IconClass={ContentIcon}
                            iconClass={itemRowIconClass}
                            title={"Comic Content & News"}
                            description={"Market reports, convention hit-lists, New release finds, Top 10s, FOC's and more."}
                        />
                    </Col>
                    <Col span={itemRowSpan}>
                        <NewMemberListItem
                            IconClass={TrendIcon}
                            iconClass={itemRowIconClass}
                            title={"Trend Tracking"}
                            description={"View Hot Comics, Daily Shakers, Weekly Movers and curated lists."}
                        />
                    </Col>
                    <Col span={itemRowSpan}>
                        <NewMemberListItem
                            IconClass={ListsIcon}
                            iconClass={itemRowIconClass}
                            title={"Key Info & Lists"}
                            description={"Get the details on what comics are key and why."}
                        />
                    </Col>
                    <Col span={itemRowSpan}>
                        <NewMemberListItem
                            IconClass={ImportCollectionIcon}
                            iconClass={itemRowIconClass}
                            title={"Import Existing Collections"}
                            description={"Dont start from scratch, Send us your existing (CSV file)"}
                        />
                    </Col>
                    <Col span={itemRowSpan}>
                        <NewMemberListItem
                            IconClass={MarketplaceIcon}
                            iconClass={itemRowIconClass}
                            title={"Aggregated Marketplace"}
                            description={"Search for comics across your favorite online stores."}
                        />
                    </Col>
                </Row>

                <div className={"greyPill clz-area"}>
                    <img src={clz_logo} className={"clz-area-logo"} />
                    <div className={"clz-area-text"}>
                        <b>Partnership with Collectorz (CLZ)</b>
                        CovrPrice’s partnership with CLZ allows our members to view our sales data across the entire CLZ ecosystem.
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default HomePageNewMemberModal;